<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import { backend_url } from '@/config/config';
import { normalizeForSearch } from '@/functions/utils';
import { formatClubNumber, isClubNumberPending } from '@/functions/clubNumber';
import { useConfirm } from '@/composables/useConfirm';

const { open: openConfirm } = useConfirm();

interface Ligue { id: number; code: string; name: string; }
interface Comite { id: number; code: string; name: string; ligue_code: string; }
interface Rep { email: string; label: string; }
interface Club {
  id: number;
  number: number;
  name: string;
  ligue: string;
  comite: string;
  comite_id?: number | null;
  short_code?: string;
  logo?: string;
  contact_email?: string;
  club_postal_code?: string | null;
  club_city?: string | null;
  helloasso_org_slug?: string;
  representatives?: Rep[];
  premium_events_enabled: boolean;
  premium_events_until?: string | null;
  dissolved?: boolean;
  founded_on?: string | null;
  founded_note?: string | null;
  seat_commune?: string | null;
  seat_department?: string | null;
}

// Lecture seule : les accès premium (et leur durée) se gèrent depuis l'écran unique
// /admin/events-premium, cf. AdminEventsPremium.vue.
const premiumBadge = (c: { premium_events_enabled: boolean; premium_events_until?: string | null }) => {
  if (!c.premium_events_enabled) return { label: 'Standard', cls: 'badge-gray' };
  if (!c.premium_events_until) return { label: 'Premium · sans limite', cls: 'badge-green' };
  const until = new Date(c.premium_events_until);
  const expired = until < new Date(new Date().toDateString());
  return { label: `${expired ? 'Expiré le' : 'Premium jusqu\'au'} ${until.toLocaleDateString('fr-FR')}`, cls: expired ? 'badge-red' : 'badge-green' };
};

const clubs = ref<Club[]>([]);
const ligues = ref<Ligue[]>([]);
const comites = ref<Comite[]>([]);
const loading = ref(true);
const search = ref('');
const filterLigue = ref('');
const filterComite = ref<number | ''>('');

const showRepModal = ref(false);
const repClub = ref<Club | null>(null);
const newRepEmail = ref('');
const savingRep = ref(false);

const allComites = ref<Comite[]>([]);
// Comité choisi sans ligue (ou dans la liste complète) : la ligue s'en déduit.
const ligueCodeOf = (comiteId: number | null) => allComites.value.find(c => c.id === comiteId)?.ligue_code || '';

const showCreateModal = ref(false);
const emptyCreateForm = () => ({
  number: null as number | null, name: '', short_code: '', comite_id: null as number | null,
  contact_email: '', club_postal_code: '', club_city: '',
});
const createForm = ref(emptyCreateForm());
const createLogoFile = ref<File | null>(null);
const createLigueCode = ref('');
const createComitesOptions = computed(() =>
  createLigueCode.value ? allComites.value.filter(c => c.ligue_code === createLigueCode.value) : allComites.value
);
const savingCreate = ref(false);

const showEditModal = ref(false);
const editClub = ref<Club | null>(null);
const editForm = ref<{ number: number | null; name: string; short_code: string; contact_email: string; club_postal_code: string; club_city: string; helloasso_org_slug: string; helloasso_client_id: string; helloasso_client_secret: string; license_info: string; comite_id: number | null; founded_on: string; founded_note: string; seat_commune: string; seat_department: string }>(
  { number: null, name: '', short_code: '', contact_email: '', club_postal_code: '', club_city: '', helloasso_org_slug: '', helloasso_client_id: '', helloasso_client_secret: '', license_info: '', comite_id: null, founded_on: '', founded_note: '', seat_commune: '', seat_department: '' }
);
const editLigueCode = ref('');
const editComitesOptions = computed(() =>
  editLigueCode.value ? allComites.value.filter(c => c.ligue_code === editLigueCode.value) : allComites.value
);
const savingEdit = ref(false);
const editLogoFile = ref<File | null>(null);
const uploadingLogo = ref(false);

const getLogoUrl = (club: Club): string => {
  if (!club.logo) return '';
  if (club.logo.startsWith('/uploads/')) return `${backend_url}${club.logo}`;
  return club.logo;
};

const filtered = computed(() => {
  const q = normalizeForSearch(search.value);
  const list = q
    ? clubs.value.filter(c =>
        normalizeForSearch(c.name).includes(q) || String(c.number).includes(q)
      )
    : clubs.value;
  return [...list].sort((a, b) => a.number - b.number);
});

const load = async () => {
  loading.value = true;
  try {
    // Clubs disparus compris (badge « Disparu ») : l'admin doit pouvoir les retrouver.
    const params: Record<string, any> = { include_dissolved: true };
    if (filterComite.value !== '') params.comite_id = filterComite.value;
    else params.all_comites = true;
    const { data } = await dartsHubApi.get('/clubs/', { params });
    clubs.value = data;
  } catch {
    toast.error('Erreur de chargement.');
  } finally {
    loading.value = false;
  }
};

const onLigueChange = async () => {
  filterComite.value = '';
  if (filterLigue.value) {
    const { data } = await dartsHubApi.get('/clubs/comites', { params: { ligue_code: filterLigue.value } });
    comites.value = data;
  } else {
    const { data } = await dartsHubApi.get('/clubs/comites');
    comites.value = data;
  }
  await load();
};

watch(filterComite, load);

const openCreateModal = () => {
  createForm.value = emptyCreateForm();
  createLigueCode.value = '';
  createLogoFile.value = null;
  showCreateModal.value = true;
};

const uploadLogo = async (clubId: number, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  await dartsHubApi.post(`/clubs/${clubId}/upload-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

const saveCreate = async () => {
  if (!createForm.value.name) {
    toast.error('Nom du club requis.');
    return;
  }
  savingCreate.value = true;
  try {
    const { data } = await dartsHubApi.post('/clubs/', {
      // Sans numéro : provisoire négatif côté backend, en attendant celui de la FFD.
      number: createForm.value.number || null,
      name: createForm.value.name,
      short_code: createForm.value.short_code || null,
      comite_id: createForm.value.comite_id,
      contact_email: createForm.value.contact_email || null,
      club_postal_code: createForm.value.club_postal_code || null,
      club_city: createForm.value.club_city || null,
    });
    if (createLogoFile.value) {
      try {
        await uploadLogo(data.id, createLogoFile.value);
      } catch {
        toast.warning('Club créé, mais l\'envoi du logo a échoué : réessayez depuis la modification.');
      }
    }
    toast.success('Club créé.');
    showCreateModal.value = false;
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    savingCreate.value = false;
  }
};

const deleteClub = async (club: Club) => {
  if (!(await openConfirm('Supprimer le club', `Supprimer le club "${club.name}" (N°${formatClubNumber(club.number)}) ? Cette action est irréversible.`))) return;
  try {
    await dartsHubApi.delete(`/clubs/${club.id}`);
    clubs.value = clubs.value.filter(c => c.id !== club.id);
    toast.success('Club supprimé.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  }
};

const openRepModal = (club: Club) => {
  repClub.value = club;
  newRepEmail.value = '';
  showRepModal.value = true;
};

const openEditModal = (club: Club) => {
  editClub.value = club;
  editForm.value = {
    number: isClubNumberPending(club.number) ? null : club.number,
    name: club.name,
    short_code: club.short_code || '',
    contact_email: club.contact_email || '',
    club_postal_code: club.club_postal_code || '',
    club_city: club.club_city || '',
    helloasso_org_slug: club.helloasso_org_slug || '',
    helloasso_client_id: '',
    helloasso_client_secret: '',
    license_info: '',
    comite_id: club.comite_id ?? null,
    founded_on: club.founded_on || '',
    founded_note: club.founded_note || '',
    seat_commune: club.seat_commune || '',
    seat_department: club.seat_department || '',
  };
  editLigueCode.value = ligueCodeOf(club.comite_id ?? null);
  editLogoFile.value = null;
  showEditModal.value = true;
};

const addRep = async () => {
  if (!newRepEmail.value || !repClub.value) return;
  savingRep.value = true;
  try {
    await dartsHubApi.post('/clubs/representatives', {
      user_email: newRepEmail.value,
      club_id: repClub.value.id,
    });
    toast.success('Représentant assigné.');
    newRepEmail.value = '';
    await load();
    repClub.value = clubs.value.find(c => c.id === repClub.value!.id) ?? null;
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    savingRep.value = false;
  }
};

const removeRep = async (email: string) => {
  try {
    await dartsHubApi.delete(`/clubs/representatives/${encodeURIComponent(email)}`);
    toast.success('Représentant retiré.');
    await load();
    if (repClub.value) repClub.value = clubs.value.find(c => c.id === repClub.value!.id) ?? null;
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  }
};

const saveEdit = async () => {
  if (!editClub.value) return;
  savingEdit.value = true;
  try {
    const payload: Record<string, any> = {};
    if (editForm.value.number) payload.number = editForm.value.number;
    if (editForm.value.name) payload.name = editForm.value.name;
    if (editForm.value.short_code !== undefined) payload.short_code = editForm.value.short_code || null;
    if (editForm.value.contact_email !== undefined) payload.contact_email = editForm.value.contact_email || null;
    // Vides ignorés : une fiche d'affiliation existante n'est jamais effacée d'ici.
    if (editForm.value.club_postal_code) payload.club_postal_code = editForm.value.club_postal_code;
    if (editForm.value.club_city) payload.club_city = editForm.value.club_city;
    payload.comite_id = editForm.value.comite_id;
    if (editForm.value.helloasso_org_slug !== undefined) payload.helloasso_org_slug = editForm.value.helloasso_org_slug || null;
    if (editForm.value.helloasso_client_id) payload.helloasso_client_id = editForm.value.helloasso_client_id;
    if (editForm.value.helloasso_client_secret) payload.helloasso_client_secret = editForm.value.helloasso_client_secret;
    if (editForm.value.license_info !== undefined) payload.license_info = editForm.value.license_info || null;
    // Page publique Histoire : date de création et siège de l'association (RNA, JO).
    payload.founded_on = editForm.value.founded_on || null;
    payload.founded_note = editForm.value.founded_note || null;
    payload.seat_commune = editForm.value.seat_commune || null;
    payload.seat_department = editForm.value.seat_department || null;
    await dartsHubApi.patch(`/clubs/${editClub.value.id}`, payload);
    if (editLogoFile.value) {
      uploadingLogo.value = true;
      await uploadLogo(editClub.value.id, editLogoFile.value);
      uploadingLogo.value = false;
    }
    toast.success('Club mis à jour.');
    showEditModal.value = false;
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    savingEdit.value = false;
    uploadingLogo.value = false;
  }
};

onMounted(async () => {
  const [liguesRes, comitesRes] = await Promise.all([
    dartsHubApi.get('/clubs/ligues'),
    dartsHubApi.get('/clubs/comites'),
  ]);
  ligues.value = liguesRes.data;
  comites.value = comitesRes.data;
  allComites.value = comitesRes.data;
  await load();
});
</script>

<template>
  <div class="page">
    <div class="page-header">
      <div style="display:flex;align-items:center;gap:0.75rem;flex-wrap:wrap">
        <h1><i class="fa-solid fa-building" /> Clubs</h1>
        <button class="btn btn-primary btn-sm" @click="openCreateModal">
          <i class="fa-solid fa-plus" /> Créer un club
        </button>
      </div>
      <div class="filters">
        <div class="search-bar">
          <i class="fa-solid fa-magnifying-glass" style="color:var(--text-muted)" />
          <input v-model="search" placeholder="Nom ou numéro…" />
        </div>
        <select v-model="filterLigue" class="filter-select" @change="onLigueChange">
          <option value="">Toutes les ligues</option>
          <option v-for="l in ligues" :value="l.code" :key="l.id">{{ l.name }} ({{ l.code }})</option>
        </select>
        <select v-model="filterComite" class="filter-select">
          <option value="">Tous les comités</option>
          <option v-for="c in comites" :value="c.id" :key="c.id">{{ c.name }} ({{ c.code }})</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>N°</th>
            <th>Logo</th>
            <th>Nom</th>
            <th>Code</th>
            <th>Ligue</th>
            <th>Comité</th>
            <th>Contact</th>
            <th>HelloAsso</th>
            <th>Premium</th>
            <th>Représentants</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="club in filtered" :key="club.id">
            <td><span class="badge badge-gray">{{ formatClubNumber(club.number) }}</span></td>
            <td>
              <img v-if="club.logo" :src="getLogoUrl(club)" class="club-logo" alt="logo" />
              <span v-else class="badge badge-gray">-</span>
            </td>
            <td class="club-name">
              {{ club.name }}
              <span v-if="club.dissolved" class="badge badge-red">Disparu</span>
              <div v-if="club.club_city" class="club-city">{{ club.club_postal_code }} {{ club.club_city }}</div>
            </td>
            <td><span v-if="club.short_code" class="badge badge-gray">{{ club.short_code }}</span><span v-else class="badge badge-gray">-</span></td>
            <td>{{ club.ligue || '-' }}</td>
            <td>{{ club.comite || '-' }}</td>
            <td>{{ club.contact_email || '-' }}</td>
            <td>
              <span v-if="club.helloasso_org_slug" class="badge badge-green">
                <i class="fa-solid fa-check" /> {{ club.helloasso_org_slug }}
              </span>
              <span v-else class="badge badge-gray">-</span>
            </td>
            <td>
              <router-link to="/admin/events-premium" class="badge" :class="premiumBadge(club).cls"
                title="Gérer les accès premium (écran unique)">
                {{ premiumBadge(club).label }}
              </router-link>
            </td>
            <td>
              <div v-if="club.representatives?.length" style="display:flex;flex-wrap:wrap;gap:0.25rem">
                <span v-for="rep in club.representatives" class="badge badge-blue" :title="rep.email" :key="rep.email">
                  {{ rep.label }}
                </span>
              </div>
              <span v-else class="badge badge-gray">-</span>
            </td>
            <td style="display:flex;gap:0.25rem">
              <button class="btn btn-ghost btn-sm" title="Modifier" @click="openEditModal(club)">
                <i class="fa-solid fa-pen" />
              </button>
              <button class="btn btn-ghost btn-sm" title="Gérer les représentants" @click="openRepModal(club)">
                <i class="fa-solid fa-user-tie" />
              </button>
              <button class="btn btn-ghost btn-sm" title="Supprimer" @click="deleteClub(club)">
                <i class="fa-solid fa-trash" />
              </button>
            </td>
          </tr>
          <tr v-if="filtered.length === 0">
            <td colspan="11" style="text-align:center;color:var(--text-muted)">Aucun club trouvé</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading" class="mobile-cards">
      <div v-for="club in filtered" class="mobile-card" :key="club.id">
        <div class="card-top">
          <div style="display:flex;align-items:center;gap:0.6rem">
            <img v-if="club.logo" :src="getLogoUrl(club)" class="club-logo" alt="logo" />
            <div>
              <div style="font-weight:600;font-size:0.95rem">
                {{ club.name }} <span v-if="club.dissolved" class="badge badge-red">Disparu</span>
              </div>
              <div style="font-size:0.78rem;color:var(--text-muted)">
                N°{{ formatClubNumber(club.number) }}<template v-if="club.short_code"> · {{ club.short_code }}</template>
              </div>
            </div>
          </div>
          <div style="display:flex;gap:0.25rem">
            <button class="btn btn-ghost btn-sm" title="Modifier" @click="openEditModal(club)">
              <i class="fa-solid fa-pen" />
            </button>
            <button class="btn btn-ghost btn-sm" title="Gérer les représentants" @click="openRepModal(club)">
              <i class="fa-solid fa-user-tie" />
            </button>
            <button class="btn btn-ghost btn-sm" title="Supprimer" @click="deleteClub(club)">
              <i class="fa-solid fa-trash" />
            </button>
          </div>
        </div>
        <div style="font-size:0.82rem;color:var(--text-muted)">
          {{ club.ligue || '-' }} / {{ club.comite || '-' }}<template v-if="club.club_city"> · {{ club.club_city }}</template>
        </div>
        <div v-if="club.contact_email" style="font-size:0.82rem;color:var(--text-muted)">
          <i class="fa-solid fa-envelope" /> {{ club.contact_email }}
        </div>
        <div>
          <span v-if="club.helloasso_org_slug" class="badge badge-green">
            <i class="fa-solid fa-check" /> {{ club.helloasso_org_slug }}
          </span>
          <span v-else class="badge badge-gray">HelloAsso non configuré</span>
        </div>
        <div>
          <router-link to="/admin/events-premium" class="badge" :class="premiumBadge(club).cls">
            {{ premiumBadge(club).label }}
          </router-link>
        </div>
        <div v-if="club.representatives?.length" style="display:flex;flex-wrap:wrap;gap:0.25rem">
          <span v-for="rep in club.representatives" class="badge badge-blue" :title="rep.email" :key="rep.email">{{ rep.label }}</span>
        </div>
      </div>
      <div v-if="filtered.length === 0" style="text-align:center;color:var(--text-muted);padding:1.5rem">Aucun club trouvé</div>
    </div>

    <div v-if="!loading" class="table-count">
      {{ filtered.length }} / {{ clubs.length }} clubs
    </div>

    <!-- Modal création club -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>Créer un club</h2>
          <button class="btn btn-ghost btn-sm" @click="showCreateModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group">
          <label>Numéro de club (facultatif)</label>
          <input v-model.number="createForm.number" type="number" min="1" placeholder="Vide si pas encore attribué par la FFD" />
        </div>
        <div class="form-group">
          <label>Nom</label>
          <input v-model="createForm.name" type="text" placeholder="Nom du club" />
        </div>
        <div class="form-group">
          <label>Code court</label>
          <input v-model="createForm.short_code" type="text" placeholder="ex: BAD" />
        </div>
        <div class="form-group">
          <label>Ligue</label>
          <select v-model="createLigueCode" class="filter-select" @change="createForm.comite_id = null">
            <option value="">-- Sélectionner --</option>
            <option v-for="l in ligues" :value="l.code" :key="l.id">{{ l.name }} ({{ l.code }})</option>
          </select>
        </div>
        <div class="form-group">
          <label>Comité</label>
          <select v-model="createForm.comite_id" class="filter-select" @change="createForm.comite_id && (createLigueCode = ligueCodeOf(createForm.comite_id))">
            <option :value="null">Aucun (club non affilié)</option>
            <option v-for="c in createComitesOptions" :value="c.id" :key="c.id">{{ c.name }} ({{ c.code }})</option>
          </select>
        </div>
        <div class="form-group">
          <label>Adresse de contact (email)</label>
          <input v-model="createForm.contact_email" type="email" placeholder="contact@club.fr" />
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Code postal</label>
            <input v-model="createForm.club_postal_code" type="text" inputmode="numeric" maxlength="5" placeholder="21500" />
          </div>
          <div class="form-group">
            <label>Ville</label>
            <input v-model="createForm.club_city" type="text" placeholder="Ville du siège" />
          </div>
        </div>
        <div class="form-group">
          <label>Logo</label>
          <input type="file" accept="image/*" @change="(e) => createLogoFile = (e.target as HTMLInputElement).files?.[0] ?? null" />
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showCreateModal = false">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="savingCreate" @click="saveCreate">
            <i v-if="savingCreate" class="fa-solid fa-spinner fa-spin" />
            Créer
          </button>
        </div>
      </div>
    </div>

    <!-- Modal édition club -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="showEditModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>Modifier le club <span class="badge badge-gray">{{ editClub?.number }}</span></h2>
          <button class="btn btn-ghost btn-sm" @click="showEditModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group">
          <label>Numéro de club</label>
          <input v-model.number="editForm.number" type="number" min="1" placeholder="En attente du numéro FFD" />
        </div>
        <div class="form-group">
          <label>Nom</label>
          <input v-model="editForm.name" type="text" placeholder="Nom du club" />
        </div>
        <div class="form-group">
          <label>Code court</label>
          <input v-model="editForm.short_code" type="text" placeholder="ex: BAD" />
        </div>
        <div class="form-group">
          <label>Adresse de contact (email)</label>
          <input v-model="editForm.contact_email" type="email" placeholder="contact@club.fr" />
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Code postal</label>
            <input v-model="editForm.club_postal_code" type="text" inputmode="numeric" maxlength="5" placeholder="21500" />
          </div>
          <div class="form-group">
            <label>Ville</label>
            <input v-model="editForm.club_city" type="text" placeholder="Ville du siège" />
          </div>
        </div>
        <div class="form-group">
          <label>Ligue</label>
          <select v-model="editLigueCode" class="filter-select" @change="editForm.comite_id = null">
            <option value="">-- Sélectionner --</option>
            <option v-for="l in ligues" :value="l.code" :key="l.id">{{ l.name }} ({{ l.code }})</option>
          </select>
        </div>
        <div class="form-group">
          <label>Comité</label>
          <select v-model="editForm.comite_id" class="filter-select" @change="editForm.comite_id && (editLigueCode = ligueCodeOf(editForm.comite_id))">
            <option :value="null">Aucun (club non affilié)</option>
            <option v-for="c in editComitesOptions" :value="c.id" :key="c.id">{{ c.name }} ({{ c.code }})</option>
          </select>
        </div>
        <div class="form-group">
          <label>HelloAsso org slug</label>
          <input v-model="editForm.helloasso_org_slug" type="text" placeholder="ex: club-badminton" />
        </div>
        <div class="form-group">
          <label>HelloAsso Client ID</label>
          <input v-model="editForm.helloasso_client_id" type="text" name="helloasso_client_id" autocomplete="off" placeholder="Laisser vide pour ne pas changer" />
        </div>
        <div class="form-group">
          <label>HelloAsso Client Secret</label>
          <input v-model="editForm.helloasso_client_secret" type="password" name="helloasso_client_secret" autocomplete="new-password" placeholder="Laisser vide pour ne pas changer" />
        </div>
        <div class="form-group">
          <label>Info licence (texte libre)</label>
          <textarea v-model="editForm.license_info" rows="3" placeholder="Tarif adhésion, informations…" style="resize:vertical" />
        </div>
        <div class="form-group">
          <label>Histoire : création de l'association</label>
          <div style="display:flex;gap:.5rem">
            <input v-model="editForm.founded_on" type="date" />
            <select v-model="editForm.founded_note" class="filter-select">
              <option value="">date exacte</option>
              <option value="au plus tard">au plus tard</option>
            </select>
          </div>
        </div>
        <div class="form-group">
          <label>Histoire : siège (commune, département)</label>
          <div style="display:flex;gap:.5rem">
            <input v-model="editForm.seat_commune" type="text" placeholder="Commune du siège" />
            <input v-model="editForm.seat_department" type="text" maxlength="3" placeholder="29" style="max-width:5rem" />
          </div>
        </div>
        <div class="form-group">
          <label>Logo</label>
          <input type="file" accept="image/*" @change="(e) => editLogoFile = (e.target as HTMLInputElement).files?.[0] ?? null" />
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showEditModal = false">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="savingEdit || uploadingLogo" @click="saveEdit">
            <i v-if="savingEdit || uploadingLogo" class="fa-solid fa-spinner fa-spin" />
            Enregistrer
          </button>
        </div>
      </div>
    </div>

    <!-- Modal représentants -->
    <div v-if="showRepModal" class="modal-overlay" @click.self="showRepModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>Représentants : {{ repClub?.name }}</h2>
          <button class="btn btn-ghost btn-sm" @click="showRepModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group">
          <div class="reps-list">
            <div v-if="!repClub?.representatives?.length" style="color:var(--text-muted);font-size:0.8rem">Aucun représentant</div>
            <div v-for="rep in repClub?.representatives" class="rep-row" :key="rep.email">
              <span class="badge badge-blue" :title="rep.email">{{ rep.label }}</span>
              <button class="btn btn-ghost btn-sm" title="Retirer" @click="removeRep(rep.email)">
                <i class="fa-solid fa-xmark" />
              </button>
            </div>
          </div>
          <div class="rep-add-row">
            <input v-model="newRepEmail" type="email" placeholder="email@exemple.fr" @keyup.enter="addRep" />
            <button class="btn btn-primary btn-sm" :disabled="savingRep || !newRepEmail" @click="addRep">
              <i v-if="savingRep" class="fa-solid fa-spinner fa-spin" /><i v-else class="fa-solid fa-plus" />
            </button>
          </div>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showRepModal = false">Fermer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filters {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.filter-select {
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  outline: none;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
  overflow-y: auto;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  min-width: 0;
  max-width: 520px;
  width: 100%;
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  margin: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.modal-header h2 {
  font-size: 1rem;
  font-weight: 600;
}

.club-name {
  font-weight: 500;
}

.club-city {
  font-size: 0.78rem;
  font-weight: 400;
  color: var(--text-muted);
}

.form-row {
  display: grid;
  grid-template-columns: 7rem 1fr;
  gap: 0.75rem;
}

.club-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: var(--bg-card);
}

.table-count {
  margin-top: 0.75rem;
  color: var(--text-muted);
  font-size: 0.8rem;
}

.reps-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.rep-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.rep-add-row {
  display: flex;
  gap: 0.5rem;
}

.rep-add-row input {
  flex: 1;
}

.badge-blue {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.mobile-cards { display: none; }

@media (max-width: 640px) {
  .table-wrapper { display: none; }
  .mobile-cards { display: flex; flex-direction: column; gap: 0.75rem; }
}

.mobile-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}
</style>
